import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sun, Moon, Monitor, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../lib/auth-store';
import { useTheme, type Theme } from '../lib/theme';

export function Topbar() {
  const user = useAuth(s => s.user);
  const logout = useAuth(s => s.logout);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function onSubmitSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/associados?q=${encodeURIComponent(search.trim())}`);
  }

  async function onLogout() {
    await logout();
    navigate('/login');
  }

  const themeIcon = theme === 'dark'
    ? <Moon className="h-4 w-4" />
    : theme === 'light'
      ? <Sun className="h-4 w-4" />
      : <Monitor className="h-4 w-4" />;

  function cycleTheme() {
    const map: Record<Theme, Theme> = { dark: 'light', light: 'system', system: 'dark' };
    setTheme(map[theme]);
  }

  return (
    <header className="h-16 border-b border-border bg-background-elevated sticky top-0 z-50 flex items-center px-6 gap-4">
      <form onSubmit={onSubmitSearch} className="flex-1 max-w-xl relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar associado, CPF ou matrícula…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-full rounded-md border border-input bg-transparent pl-10 pr-3 text-sm placeholder:text-foreground-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition-colors"
        />
      </form>

      <div className="flex items-center gap-2">
        <button
          onClick={cycleTheme}
          className="inline-flex items-center justify-center h-10 w-10 rounded-md text-foreground-muted hover:bg-background-muted hover:text-foreground transition-colors"
          title={`Tema: ${theme}`}
        >
          {themeIcon}
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2.5 h-10 px-2 rounded-md hover:bg-background-muted transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
              {user?.nome?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium leading-tight">{user?.nome ?? 'Usuário'}</p>
              <p className="text-xs text-foreground-muted leading-tight uppercase tracking-wide">{user?.roleCode ?? '—'}</p>
            </div>
            <ChevronDown className="h-3 w-3 text-foreground-muted" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-popover border border-border rounded-md shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium">{user?.nome}</p>
                <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
              </div>
              <button
                onClick={onLogout}
                className="w-full px-4 py-2.5 text-sm text-left hover:bg-background-muted transition-colors flex items-center gap-2 text-foreground"
              >
                <LogOut className="h-4 w-4" />Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
